/**
 * Pure helpers shared by the public gift page (/gift/:token) and its tests.
 * Nothing here may touch the network, the Shopify code, or any PII.
 */

/** Opaque view tokens are base64url of >= 32 random bytes. */
export const GIFT_TOKEN_MIN_LENGTH = 32;
export const GIFT_TOKEN_MAX_LENGTH = 256;

const TOKEN_RE = /^[A-Za-z0-9_-]+$/;

export function isValidPublicToken(token: unknown): boolean {
  if (typeof token !== 'string') return false;
  const value = token.trim();
  if (value.length < GIFT_TOKEN_MIN_LENGTH || value.length > GIFT_TOKEN_MAX_LENGTH) return false;
  return TOKEN_RE.test(value);
}

/**
 * The only value the QR may ever encode: the canonical public page URL built
 * from the site origin and the SAME opaque token. Never the Shopify code, the
 * token hash, a database id or any personal detail.
 */
export function publicGiftPageUrl(origin: string, token: string): string {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/gift/${token}`;
}

/** Fields the public endpoint is allowed to return. Used as a test allowlist. */
export const PUBLIC_CARD_FIELDS = [
  'last_characters',
  'initial_amount',
  'balance_amount',
  'balance_available',
  'balance_synced_at',
  'currency',
  'status',
  'expires_on',
  'recipient_name',
  'sender_name',
  'message',
  'occasion',
  'template',
] as const;

/** Fields that must never appear in a public response. */
export const PUBLIC_CARD_FORBIDDEN_FIELDS = [
  'id',
  'gift_card_id',
  'code',
  'code_encrypted',
  'token_encrypted',
  'token_hash',
  'shopify_gift_card_gid',
  'recipient_email',
  'recipient_phone',
  'internal_note',
  'email_hint',
  'requires_email_confirm',
] as const;

export function formatGiftAmount(value: number | null | undefined, currency = 'ILS'): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: currency || 'ILS',
    maximumFractionDigits: 0,
  }).format(Number(value));
}

export function formatGiftDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

export interface BalanceView {
  available: boolean;
  /** Never falls back to the original amount. */
  amount: number | null;
  syncedAt: string | null;
}

/**
 * Resolves the displayed balance. When Shopify could not be refreshed the
 * balance is reported as unavailable — the initial amount is NEVER reused as if
 * it were the current balance.
 */
export function resolveBalanceView(card: {
  balance_available?: boolean | null;
  balance_amount?: number | null;
  balance_synced_at?: string | null;
}): BalanceView {
  const available = card.balance_available === true && typeof card.balance_amount === 'number';
  return {
    available,
    amount: available ? Number(card.balance_amount) : null,
    syncedAt: card.balance_synced_at ?? null,
  };
}
